import { takeLatest, all, put, fork, select, call } from 'redux-saga/effects';
import {
  setRelevantWebsites,
  setRecommendedApplications,
  setUsedApplications,
  setAuthorizedApplications,
  setRelevantWebsitesPreferences as setRelevantWebsitesPreferencesAction,
  setLoadedFalse as setLoadedFalseAction,
  setRelevantWebsitesLoaded,
  setApplicationsLoaded
} from './actions';
import {
  getRelevantWebsites,
  getRelevantWebsitesPreferences,
  RelevantWebsitePreference,
  updateUsedApplicationsPreferences,
  updateRecommendedApplicationsPreferences,
  updateRelevantWebsitesPreferences,
  RelevantWebsite
} from '../../api/user';
import { DashboardItemsActionType } from './types';
import { AppState } from "../";
import { getUserApplicationsProfile } from '../../api/application';
import { authenticationClient } from '../../Providers/AuthenticationProvider';
import settings from '../../config/settings';
import { isMobileDevice } from '../../utils';
import { CulturesActionType } from '../cultures/types';


export function* loadUserApplications() {

  if (!authenticationClient) {
    return;
  }

  const isAuthenticated: boolean = yield authenticationClient.isAuthenticated();

  if (!isAuthenticated) {
    return;
  }

  yield loadUserApplicationsProfile();
  yield loadRelevantWebsites();
}

export function* loadUserApplicationsProfile(): Generator<any, void, any> {
  const getState = (state: AppState) => state;
  const { cultures } = yield select(getState);

  const isAuthenticated: boolean = yield authenticationClient.isAuthenticated();

  if (!isAuthenticated) {
    return;
  }

  const accessTokenMyDanfossApi: string = yield authenticationClient.getTokenSilently({ audience: settings.myDanfossAPI.audience, analytics: false });

  try {
    const userApplicationsProfile = yield getUserApplicationsProfile(accessTokenMyDanfossApi, isMobileDevice(), cultures.currentCulture);

    yield all([
      put(setUsedApplications(userApplicationsProfile.used_applications)),
      put(setRecommendedApplications(userApplicationsProfile.recommended_applications)),
      put(setAuthorizedApplications(userApplicationsProfile.authorized_applications)),
      put(setApplicationsLoaded({ loaded: true, hasError: false })),
    ]);

  } catch (error) {
    // In case we get an error loading applications, set as loaded to prevent the
    // dashboard description to not being shown.
    yield put(setApplicationsLoaded({ loaded: true, hasError: true }));
  }
}

export function* loadRelevantWebsites() {
  const getState = (state: AppState) => state;
  const { profile, cultures } = yield select(getState);

  const isAuthenticated: boolean = yield authenticationClient.isAuthenticated();

  if (!isAuthenticated) {
    return;
  }

  const accessTokenMyDanfossApi: string = yield authenticationClient.getTokenSilently({ audience: settings.myDanfossAPI.audience, analytics: false });

  try {

    let [relevantWebsites, relevantWebsitesPreferences] = yield all([
      getRelevantWebsites(cultures.currentCulture, profile.country.id, accessTokenMyDanfossApi),
      getRelevantWebsitesPreferences(accessTokenMyDanfossApi, isMobileDevice())
    ]);

    const relevantWebsitesWithPreferences = relevantWebsites.relevant_websites.map((relevantWebsite: RelevantWebsite) => {
      const preference = relevantWebsitesPreferences.preferences.filter((pref: RelevantWebsitePreference) => pref.id === relevantWebsite.id)
      return { ...relevantWebsite, ...preference[0] };
    })

    yield all([
      put(setRelevantWebsitesPreferencesAction(relevantWebsitesPreferences.preferences)),
      put(setRelevantWebsites(relevantWebsitesWithPreferences)),
    ]);

    yield put(setRelevantWebsitesLoaded({ loaded: true, hasError: false }));

  } catch (error) {
    // In case we get an error loading relevant applications, set as loaded to prevent the
    // dashboard description to not being shown.
    yield put(setRelevantWebsitesLoaded({ loaded: true, hasError: true }))
  }
}

export function* updateUserPreferences(action: any) {
  const { isMobile, used_application_preferences, recommended_application_preferences, relevant_websites_preferences } = action.payload;

  const isAuthenticated: boolean = yield authenticationClient.isAuthenticated();

  if (!isAuthenticated) {
    return;
  }

  const accessTokenMyDanfossApi: string = yield authenticationClient.getTokenSilently({ audience: settings.myDanfossAPI.audience, analytics: false });

  yield put(setLoadedFalseAction());

  try {
    yield updateUsedApplicationsPreferences(accessTokenMyDanfossApi, used_application_preferences, isMobile);
    yield updateRecommendedApplicationsPreferences(accessTokenMyDanfossApi, recommended_application_preferences, isMobile);
    yield updateRelevantWebsitesPreferences(accessTokenMyDanfossApi, relevant_websites_preferences, isMobile);

    yield all([
      call(loadUserApplicationsProfile),
      call(loadRelevantWebsites),
    ]);

  } catch (error) {
    // In case we get an error loading preferences, set as loaded to prevent the
    // dashboard description to not being shown.
    yield put(setApplicationsLoaded({ loaded: true, hasError: true }));
    yield put(setRelevantWebsitesLoaded({ loaded: true, hasError: true }));
  }
}

export function* watchLoadUserApplications() {
  yield takeLatest(DashboardItemsActionType.GET_USED_APPLICATIONS, loadUserApplicationsProfile);
}

export function* watchLoadRelevantWebsites() {
  yield takeLatest(DashboardItemsActionType.GET_RELEVANT_WEBSITES, loadRelevantWebsites);
}

export function* watchUpdateUserPreferences() {
  yield takeLatest(DashboardItemsActionType.UPDATE_USER_PREFERENCES, updateUserPreferences);
}

export function* watchClutureChange() {
  yield takeLatest(CulturesActionType.CULTURE_CHANGE, loadUserApplications);
}

export default function* dashboardItemsSagas() {
  yield all([
    fork(watchLoadUserApplications),
    fork(watchLoadRelevantWebsites),
    fork(watchUpdateUserPreferences),
    fork(watchClutureChange)
  ]);
}
